import * as React from "react";
import { CommonHelper } from "../../../../../../../react-core/Helpers/CommonHelper";
import { MoneyDisplayHelper } from "../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { CheckboxEditor } from "../../../../../../../react-core/Html/CheckboxEditor";
import { PartLoading } from "../../../../../../../react-core/Html/PartLoading";
import { PercentageTextBoxEditor } from "../../../../../../../react-core/Html/PercentageTextBoxEditor";
import { isNullOrEmpty } from "../../../../../../../react-core/Utils/Arrays";
import { DrawdownType } from "../../../../../../../Wrap/Constants/Enums/Drawdown/DrawdownType";
import { InvestmentAllocation } from "../../../../../../../Wrap/Models/Domains/Investment/InvestmentAllocation";
import { PensionTransfer } from "../../../../../../../Wrap/Models/Models/Processes/Quote/Pension/PensionTransfer";
import { DrawdownModel } from "../../../../../../../Wrap/Models/Models/QuoteAndApplyBase/Shared/DrawdownModel";
import { User } from "../../../../../../../Wrap/Models/Models/User/User";
import { GENERIC_RESTRICTIONS_MESSAGE } from "../../../../../../Shared/Common/UserMessages";
import { Alert } from "../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../Shared/Entities/Constants/AlertConstants";
import { ProcessType } from "../../../../../../Shared/IST/Resource/Content";
import { IProcessComponentProps, ProcessComponent } from "../../../../../../Shared/Process/Process/ProcessComponent";
import { Table } from "../../../../../../Shared/Table/Table";
import { UserStore } from "../../../../../Stroes/Variable";
import { RestrictionStore } from "../../Restrictions/Variable";
import { getAssetsToSellTotalForIncomeFromIncome } from "../IncomePaymentDetails/Helper/IncomePaymentHelper";
import { calculateChooseAssetsToSellTotal } from "./Helper/DrawdownDetailHelper";
import { ChooseAssetsToSellFor } from "./Resource/Contents";
import { ChooseAssetsToSellAssets } from "./Store/DrawdownCalculationStoreStatic";
import { DeferredFundWarningMessage } from "../../Resource/Contents";
import { ProductType } from "../../../../../../../Wrap/Constants/Enums/Domain/ProductType";
import { PensionContribution } from "../../../../../../../Wrap/Models/Models/Processes/Apply/Pension/PensionContribution";
import { DrawdownIncomeAmendmentInstruction } from "../../../../../../../Wrap/Models/Domains/Drawdown/DrawdownIncomeAmendmentInstruction";

interface IChooseAssetsToSellTableProps extends IProcessComponentProps {
    data: InvestmentAllocation[];
    tableData: ChooseAssetsToSellAssets[];
    modelData: DrawdownModel;
    reason: ChooseAssetsToSellFor;
    processType: ProcessType;
    gadReviewtotalValue?: number;
    transferData?: PensionTransfer[];
    isAcrossAllAssets?: boolean;
    disableOffPlatform?: boolean;
    pensionBase?: PensionContribution;
    drawdownIncomeAmendmentInstruction?: DrawdownIncomeAmendmentInstruction;
}

interface IChooseAssetsToSellTableState {
    userInfo: User;
    validationErrors: string[];
}

export class ChooseAssetsToSellTable extends ProcessComponent<
    IChooseAssetsToSellTableProps,
    IChooseAssetsToSellTableState
> {
    constructor(props: any) {
        super(props);
        this.state = {
            userInfo: UserStore.getUserInfo(),
            validationErrors: [],
        };
    }

    private onAssetsChanges(row: ChooseAssetsToSellAssets, v: number) {
        const i = this.props.data?.findIndex((ele) => ele.ProductId == row.Id);
        const _data = this.props.data.concat();
        if (i > -1) {
            _data[i].Percentage = v;
        } else {
            _data.push({ Name: row.Assets, Percentage: v, ProductId: row.Id, Type: row.ProductType });
        }
        this.props.onChange(_data);
    }

    isValid() {
        const precision = 100;
        const hasValidationErrors = Object.values(this.state.validationErrors).some(errors => errors !== null && errors.length > 0);
        const total = this.props.data?.reduce((sum, curr) => sum + curr.Percentage * precision, 0) / precision;
        let errorMessage = ["Please enter values against the assets to disinvest that total 100%"];

        if (this.props.processType === ProcessType.EditIncomeDrawdown && total !== 100) {
            errorMessage = ["The total allocations for the regular drawdown strategy must equal 100%"];
        }

        if (total !== 100) {
            this.props.showErrorMsgPopUp(errorMessage);
            return false;
        }

        if (super.isValid() && total === 100 && hasValidationErrors) {
            this.props.showErrorMsgPopUp(this.state.validationErrors);
            return false;
        }
        return true;
    }

    disableInput = (row) => {
        return row.HasOpenValueSellOrder
            || row.DisinvestAll
            || (this.props.disableOffPlatform && row.ProductType == ProductType.OffPlatform)
            || row.MarketValue.Amount <= 0;
    };

    disableSellALL = (row) => {
        return row.HasOpenValueSellOrder
            || (this.props.disableOffPlatform && row.ProductType == ProductType.OffPlatform)
            || row.MarketValue.Amount <= 0;
    };

    checkDeferredFunds = (assets) => {
        let deferredInstrumentCodes = assets.filter((a) => a.IsDeferred).map((o) => o.Assets);
        let message = DeferredFundWarningMessage.CannotSwitchFromDeferredFundWarning.replace(
            "<InstrumentCode>",
            deferredInstrumentCodes.join(",")
        );
        return message;
    };

    handleValidation = (index: number, errors: string[]) => {
        this.setState(prevState => ({
            validationErrors: errors
        }));
    }

    public render(): JSX.Element {
        const { data, tableData, modelData, reason, processType, transferData } = this.props;
        const total = data?.reduce((sum, curr) => {
            return (sum = sum + curr.Percentage);
        }, 0);

        let totalValue;
        if (processType == ProcessType.GadReview) {
            totalValue = this.props.gadReviewtotalValue;
        } else if (modelData && modelData.DrawdownType == DrawdownType.Regular) {
            totalValue = modelData?.RegularCrystallisationDetails?.Amount?.Amount;
        } else {
            if (modelData) {
                totalValue = calculateChooseAssetsToSellTotal(modelData, reason, this.props.drawdownIncomeAmendmentInstruction, this.props.pensionBase);
            } else if (transferData?.length > 0) {
                totalValue = getAssetsToSellTotalForIncomeFromIncome(
                    transferData[0]?.IncomePaymentDetails?.DrawdownIncome?.AnnualIncome
                );
            }
        }

        if (isNullOrEmpty(tableData)) {
            return <PartLoading isLoading={true} />;
        }

        let _tableData = tableData.concat();

        if (RestrictionStore.sellDisabled() && processType !== ProcessType.WhatIf) {
            _tableData = [];
        }

        const investmentsWithOpenSellOrder =
            _tableData.length > 0 ? _tableData.filter((ele) => ele.HasOpenValueSellOrder) : [];

        let restrictedMessage = "An account with suspended or restricted activities cannot setup sell investments";

        if (this.state.userInfo != null && this.state.userInfo.IsAdviser) {
            restrictedMessage = GENERIC_RESTRICTIONS_MESSAGE;
        }

        return (
            <>
                <hr />
                {(RestrictionStore.sellDisabled() && processType !== ProcessType.WhatIf) && <Alert alertType={AlertType.Error} message={restrictedMessage} />}
                {investmentsWithOpenSellOrder.length > 0 && (
                    <Alert
                        alertType={AlertType.Warning}
                        message={`Instrument ${investmentsWithOpenSellOrder
                            .map((ele) => ele.Assets)
                            .toString()} currently has an open value sell order. No further sell instructions can be placed until this has been confirmed.`}
                    />
                )}
                    <Table
                        data={_tableData}
                        col={[
                            {
                                title: "Assets",
                                key: "Assets",
                                render: (data, row: ChooseAssetsToSellAssets, index) => {
                                    return (
                                        <a
                                            className={CommonHelper.isNullOrEmpty(row.FactsheetUrl) ? "" : "hyper-link"}
                                            href={row.FactsheetUrl}
                                            target="_blank"
                                        >
                                            {data}
                                        </a>
                                    );
                                },
                            },
                            {
                                title: "Current estimated value",
                                key: "MarketValue",
                                render: (data, row, index) => {
                                    return MoneyDisplayHelper.getMoneyFormat(data);
                                },
                            },
                            {
                                title: "Percentage of disinvestment amount",
                                thCssWrapper: "cell-right cell-15 pr-0",
                                key: "",
                                render: (data, row: ChooseAssetsToSellAssets, index) => {
                                    const disableRow =
                                        (this.props.disableOffPlatform && row.ProductType == ProductType.OffPlatform)
                                        || row.MarketValue.Amount <= 0
                                        || row.HasOpenValueSellOrder;
                                    const percentage = disableRow ? 0 :
                                        (this.props.data?.find((ele) => ele.ProductId == row.Id)?.Percentage || null);
                                    let totalHoldingsValue = 0;
                                    this.props.tableData.forEach((ele) => {
                                        if(!disableRow) {
                                            totalHoldingsValue += ele.MarketValue.Amount;
                                        }
                                    });

                                    return (
                                        <PercentageTextBoxEditor
                                            value={parseFloat((percentage || 0).toFixed(2))}
                                            onChange={(v) => this.onAssetsChanges(row, v)}
                                            maxNumberOfDecimalPlaces={{
                                                errorMessage: "only allow two decimal places",
                                                value: 2,
                                            }}
                                            disabled={this.disableInput(row)}
                                            validationRules={[
                                                {
                                                    errorMessage:
                                                        "Please enter a value to disinvest that is less than 100% and equates to less than 100% of the current estimated value of the fund, or select to disinvest all units from the asset.",
                                                    validator: (v) => {
                                                        return v <= 100;
                                                    },
                                                },
                                                {
                                                    errorMessage:
                                                        "Product allocation value exceeds 95% of total holding value.",
                                                    validator: (v) => {
                                                        let amount = (v || 0) * totalValue * 0.01;
                                                        return (
                                                            amount < row.MarketValue?.Amount * 0.95 || row.DisinvestAll
                                                        );
                                                    },
                                                },
                                                {
                                                    errorMessage:
                                                        "You have entered a value to disinvest that is greater than the current estimated value of the asset.",
                                                    validator: (v) => {
                                                        let amount = (v || 0) * totalValue * 0.01;
                                                        amount = Number(amount.toFixed(2));
                                                        return amount <= row.MarketValue?.Amount;
                                                    },
                                                },
                                                {
                                                    errorMessage:
                                                        "You have entered a value that is equal to the current estimated value of the asset. You may either choose to 'disinvest all' or reduce the value that you wish to disinvest.",
                                                    validator: (v) => {
                                                        if (modelData?.DrawdownType == DrawdownType.Regular) {
                                                            return true;
                                                        }
                                                        let amount = (v || 0) * totalValue * 0.01;
                                                        amount = Number(amount.toFixed(2));
                                                        return (
                                                            amount !== row.MarketValue?.Amount ||
                                                            (amount == row.MarketValue?.Amount && row.DisinvestAll)
                                                        );
                                                    },
                                                },
                                                {
                                                    errorMessage:
                                                        "Please enter an amount less than the proportional holding value of this fund.",
                                                    validator: (v) => {
                                                        if (row.IsDeferred) {
                                                            return (
                                                                (v || 0) <=
                                                                (Math.floor(
                                                                    (row.MarketValue.Amount / totalHoldingsValue) *
                                                                        100 *
                                                                        100
                                                                ) / 100 || 0)
                                                            );
                                                        }

                                                        return true;
                                                    },
                                                },
                                            ]}
                                            onValidation={(errors) => this.handleValidation(index, errors)}
                                            infoText={
                                                modelData?.DrawdownType != DrawdownType.Regular
                                                    ? MoneyDisplayHelper.getMoneyFormat({
                                                          Amount: row.DisinvestAll
                                                            ? row.MarketValue.Amount 
                                                            : (percentage || 0) * totalValue * 0.01,
                                                      })
                                                    : null
                                            }
                                        />
                                    );
                                },
                            },
                            modelData?.DrawdownType != DrawdownType.Regular
                                ? {
                                      thCssWrapper: "cell-right pr-0",
                                      tdCssWrapper: "cell-right pr-0",
                                      title: "Disinvest all",
                                      key: "DisinvestAll",
                                      render: (data, row: ChooseAssetsToSellAssets, index) => {
                                          const percentage =
                                              this.props.data?.find((ele) => ele.ProductId == row.Id)?.Percentage || 0;
                                          return (
                                              <CheckboxEditor
                                                  cssWrapper={"square-checkbox mb-0"}
                                                  onChange={(v) => {
                                                      this.onAssetsChanges(row, v
                                                        ? (Math.floor((row.MarketValue.Amount / totalValue) * 100 * 100) / 100 || 0) > 100
                                                            ? 100
                                                            : Math.floor((row.MarketValue.Amount / totalValue) * 100 * 100) / 100 || 0
                                                        : percentage);

                                                      this.props.tableData[index].DisinvestAll = v;
                                                  }}
                                                  value={data}
                                                  disabled={this.disableSellALL(row)}
                                              />
                                          );
                                      },
                                  }
                                : null,
                        ]}
                        TotalBody={[
                            { data: "Total", colSpan: 2 },
                            {
                                data: (
                                    <div style={{ textAlign: 'right' }}>
                                        <span className={total == 100 ? "text-positive" : "text-text"}>{total}%</span>
                                    </div>
                                ),
                                colSpan: 1,
                            },
                            { data: "", colSpan: 1 }
                        ]}
                    />
            </>
        );
    }
}
